import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { TransactionsActions } from './transaction-feature.actions';
import {
  FilterValues,
  FinancialAccountActivityItem,
  FinancialAccountJournalActivityItem,
  TransactionDetails,
  TransactionHistory,
  TransactionListItem,
} from '@shared/models';

export interface TransactionsState {
  transactions: TransactionListItem[];
  transactionsTotalElements: number;
  transactionsActiveFilters: FilterValues | undefined;
  isLoading: boolean;
  businessAccountTransactions: TransactionListItem[];
  businessAccountTransactionsTotalElements: number;
  businessAccountTransactionsActiveFilters: FilterValues | undefined;
  businessAccountTransactionsLoading: boolean;
  transactionDetails: TransactionDetails | null;
  transactionDetailsLoading: boolean;
  transactionHistory: TransactionHistory[] | null;
  journalActivity: FinancialAccountJournalActivityItem[];
  journalActivityTotalElements: number;
  journalActivityActiveFilters: FilterValues | null;
  journalActivityLoading: boolean;
  ledgerEntries: FinancialAccountActivityItem[];
  ledgerEntriesTotalElements: number;
  ledgerEntriesActiveFilters: FilterValues | undefined;
  ledgerEntriesLoading: boolean;
}

export const transactionsInitialState: TransactionsState = {
  transactions: [],
  transactionsTotalElements: 0,
  transactionsActiveFilters: { date: 'DEFAULT' },
  isLoading: false,
  businessAccountTransactions: [],
  businessAccountTransactionsTotalElements: 0,
  businessAccountTransactionsActiveFilters: { date: 'DEFAULT' },
  businessAccountTransactionsLoading: false,
  transactionDetails: null,
  transactionDetailsLoading: false,
  transactionHistory: null,
  journalActivity: [],
  journalActivityTotalElements: 0,
  journalActivityActiveFilters: { date: 'DEFAULT' },
  journalActivityLoading: false,
  ledgerEntries: [],
  ledgerEntriesTotalElements: 0,
  ledgerEntriesActiveFilters: { date: 'DEFAULT' },
  ledgerEntriesLoading: false,
};

export const transactionFeature = createFeature({
  name: 'transactionFeature',
  reducer: createReducer(
    transactionsInitialState,
    on(
      TransactionsActions.loadTransactions,
      (state): TransactionsState => ({ ...state, transactions: [], transactionsTotalElements: 0, isLoading: true }),
    ),
    on(TransactionsActions.loadTransactionsSuccess, (state, { transactions, totalElements }): TransactionsState => {
      return { ...state, transactions, transactionsTotalElements: totalElements, isLoading: false };
    }),
    on(TransactionsActions.loadTransactionsFailure, (state): TransactionsState => {
      return { ...state, transactions: [], transactionsTotalElements: 0, isLoading: false };
    }),
    on(
      TransactionsActions.setTransactionsActiveFilters,
      (state, { filters }): TransactionsState => ({ ...state, transactionsActiveFilters: filters }),
    ),
    on(
      TransactionsActions.loadBusinessAccountTransactions,
      (state): TransactionsState => ({ ...state, transactions: [], transactionsTotalElements: 0, isLoading: true }),
    ),
    on(TransactionsActions.loadBusinessAccountTransactionsSuccess, (state, { transactions, totalElements }): TransactionsState => {
      return { ...state, transactions, transactionsTotalElements: totalElements, isLoading: false };
    }),
    on(TransactionsActions.loadBusinessAccountTransactionsFailure, (state): TransactionsState => {
      return { ...state, transactions: [], transactionsTotalElements: 0, isLoading: false };
    }),
    on(
      TransactionsActions.setBusinessAccountTransactionsActiveFilters,
      (state, { filters }): TransactionsState => ({ ...state, transactionsActiveFilters: filters }),
    ),
    on(TransactionsActions.loadTransactionDetails, (state): TransactionsState => {
      return { ...state, transactionDetails: null, transactionDetailsLoading: true };
    }),
    on(TransactionsActions.loadTransactionDetailsSuccess, (state, { transactionDetails }): TransactionsState => {
      return { ...state, transactionDetails: transactionDetails, transactionDetailsLoading: false };
    }),
    on(TransactionsActions.loadTransactionDetailsFailure, (state): TransactionsState => {
      return { ...state, transactionDetails: null, transactionDetailsLoading: false };
    }),
    on(TransactionsActions.loadTransactionHistorySuccess, (state, { transactionHistory }): TransactionsState => {
      return { ...state, transactionHistory: transactionHistory };
    }),
    on(TransactionsActions.loadTransactionHistoryFailure, (state): TransactionsState => {
      return { ...state, transactionHistory: [] };
    }),
    on(
      TransactionsActions.loadJournalActivity,
      (state): TransactionsState => ({
        ...state,
        journalActivity: [],
        journalActivityTotalElements: 0,
        journalActivityLoading: true,
      }),
    ),
    on(
      TransactionsActions.loadJournalActivitySuccess,
      (state, { items, totalElements }): TransactionsState => ({
        ...state,
        journalActivity: items,
        journalActivityTotalElements: totalElements,
        journalActivityLoading: false,
      }),
    ),
    on(TransactionsActions.loadJournalActivityFailure, (state): TransactionsState => ({ ...state, journalActivityLoading: false })),
    on(
      TransactionsActions.setJournalActivityActiveFilters,
      (state, { filters }): TransactionsState => ({ ...state, journalActivityActiveFilters: filters }),
    ),
    on(
      TransactionsActions.loadLedgerEntries,
      (state): TransactionsState => ({
        ...state,
        ledgerEntries: [],
        ledgerEntriesTotalElements: 0,
        ledgerEntriesLoading: true,
      }),
    ),
    on(
      TransactionsActions.loadLedgerEntriesSuccess,
      (state, { items, totalElements }): TransactionsState => ({
        ...state,
        ledgerEntries: items,
        ledgerEntriesTotalElements: totalElements,
        ledgerEntriesLoading: false,
      }),
    ),
    on(TransactionsActions.loadLedgerEntriesFailure, (state): TransactionsState => ({ ...state, journalActivityLoading: false })),
    on(
      TransactionsActions.setLedgerEntriesActiveFilters,
      (state, { filters }): TransactionsState => ({ ...state, ledgerEntriesActiveFilters: filters }),
    ),
  ),
  extraSelectors: ({
    selectTransactionsActiveFilters,
    selectBusinessAccountTransactionsActiveFilters,
    selectJournalActivityActiveFilters,
    selectLedgerEntriesActiveFilters,
  }) => ({
    selectTransactionsFiltersRequest: createSelector(selectTransactionsActiveFilters, (activeFilters) => getActiveFilters(activeFilters)),
    selectTransactionsActiveFiltersCount: createSelector(selectTransactionsActiveFilters, (activeFilters) =>
      getActiveFiltersCount(activeFilters),
    ),
    selectBusinessAccountTransactionsFiltersRequest: createSelector(selectBusinessAccountTransactionsActiveFilters, (activeFilters) =>
      getActiveFilters(activeFilters),
    ),
    selectBusinessAccountTransactionsActiveFiltersCount: createSelector(selectBusinessAccountTransactionsActiveFilters, (activeFilters) =>
      getActiveFiltersCount(activeFilters),
    ),
    selectJournalActivityFiltersRequest: createSelector(selectJournalActivityActiveFilters, (activeFilters) =>
      getActiveFilters(activeFilters),
    ),
    selectJournalActivityActiveFiltersCount: createSelector(selectJournalActivityActiveFilters, (activeFilters) =>
      getActiveFiltersCount(activeFilters),
    ),
    selectLedgerEntriesFiltersRequest: createSelector(selectLedgerEntriesActiveFilters, (activeFilters) => getActiveFilters(activeFilters)),
    selectLedgerEntriesActiveFiltersCount: createSelector(selectLedgerEntriesActiveFilters, (activeFilters) =>
      getActiveFiltersCount(activeFilters),
    ),
  }),
});

const getActiveFilters = (activeFilters?: FilterValues | null) => {
  if (!activeFilters) {
    return;
  }
  const filters: Record<string, string> = {};
  Object.keys(activeFilters).map((filterKey) => {
    const value = activeFilters[filterKey];
    if (value && value !== 'all') {
      filters[filterKey] = value;
    }
  });
  return filters;
};

const getActiveFiltersCount = (activeFilters?: FilterValues | null) => {
  if (!activeFilters) {
    return 0;
  }
  return Object.values(activeFilters).filter((value) => !!value && value !== 'all').length;
};

export const selectTransactionDetailsWithHistory = createSelector(
  transactionFeature.selectTransactionDetails,
  transactionFeature.selectTransactionHistory,
  (transactionDetails, transactionHistory) => {
    if (!transactionDetails || !transactionHistory) {
      return;
    }

    return {
      transactionDetails,
      transactionHistory,
    };
  },
);
